import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowDownToLine, ArrowLeft } from "lucide-react";
import { SubmittedAuthenticationForm } from "@/lib/types";
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { AuthenticationFormProps } from "@/components/cms/AuthenticationForm";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/axiosConfig.ts";
import { downloadPdf } from "@/api/cms.ts";

interface DocumentItem {
  id: string;
  title: string;
  weight: number; // en Mo
  downloadUrl?: string;
}

interface DocumentListProps {
  documents: DocumentItem[];
  instanceId: string;
}

const DocumentList = ({ documents, instanceId }: DocumentListProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const handleDownloadDocument = async (doc: DocumentItem) => {
    try {
      const response = await api.post<string>(`/api/back/artwork-authentication-attachment/link`, {
        fileName: doc.title,
        instanceId: instanceId,
      });

      // Récupérer le fichier en tant que blob
      const fileResponse = await fetch(response.data);
      const blob = await fileResponse.blob();

      // Créer une URL locale et télécharger
      const url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = doc.title;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(url);

      toast({
        title: t("success") || "Succès",
        description: t("cms.authentication.detail.document.downloaded") || "Document téléchargé",
        variant: "success",
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      toast({
        title: t("error") || "Erreur",
        description: t("cms.authentication.detail.document.error") || "Erreur lors du téléchargement",
        variant: "error",
      });
    }
  };

  const formatWeight = (weight: number): string => {
    return `${weight.toFixed(2)} Mo`;
  };

  if (documents.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <p className="text-muted-foreground">
          {t("cms.authentication.detail.noDocuments") || "Aucun document"}
        </p>
      </div>
    );
  }

  const formatTitle = (title: string): string => {
    return title.split('-').filter((_, index) => index === 0 || index > 4).join('-');
  }

  return (
    <div className="flex flex-col" style={{ gap: "4px" }}>
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="w-full bg-white border border-gray-200 rounded-md py-2 px-4 flex items-center justify-between"
        >
          <div className="flex flex-col">
            <h5 className="font-semibold text-[#0c111d] font-['Raleway']">
              {formatTitle(doc.title)}
            </h5>
            <p className="text-xs text-muted-foreground">
              {formatWeight(doc.weight)}
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={() => handleDownloadDocument(doc)}
            disabled={!doc.title}
            className="flex items-center gap-2 bg-transparent"
          >
            <ArrowDownToLine className="h-6 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};

const ArtworkAuthenticationDetailCMS = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submission, setSubmission] = useState<SubmittedAuthenticationForm | null>(null);
  const [_loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!id) {
        toast({
          title: t("error"),
          description: t("cms.authentication.detail.error.noId"),
          variant: "error",
        });
        navigate("/cms/artwork-authentication");
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/back/api/artwork-authentication/one/${id}`);
        const data = response.data;

        const transformedSubmission: SubmittedAuthenticationForm = {
          ...data,
          images: data.images?.map((img: any) => ({
            id: img.id || img._id || String(Math.random()),
            title: img.link || 'Image sans titre',
            weight: img.size / (1024 * 1024) || 0, // en Mo
            downloadUrl: img.downloadUrl || img.url || img.s3Url,
          })) || [],
          documents: data.documents?.map((doc: any) => ({
            id: doc.id || doc._id || String(Math.random()),
            title: doc.link || 'Document sans titre',
            weight: doc.size / (1024 * 1024) || 0, // en Mo
            downloadUrl: doc.downloadUrl || doc.url || doc.s3Url,
          })) || [],
        };

        setSubmission(transformedSubmission);
      } catch (error) {
        console.error("Error fetching submission:", error);
        toast({
          title: t("error"),
          description: t("cms.authentication.detail.error.fetch"),
          variant: "error",
        });
        navigate("/cms/artwork-authentication");
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [id, navigate, toast, t]);

  const updateStatus = async (newIsPaid: boolean) => {
    if (!submission || !id) return;
    try {
      await api.patch(`/back/api/artwork-authentication/${id}`, { isPaid: newIsPaid });

      setSubmission({
        ...submission,
        isPaid: newIsPaid,
      });

      toast({
        title: t("success") || "Succès",
        description: newIsPaid
          ? (t("cms.authentication.detail.status.updated.paid") || "Statut mis à jour : Payé")
          : (t("cms.authentication.detail.status.updated.unpaid") || "Statut mis à jour : Non payé"),
        variant: "success",
      });
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: t("error") || "Erreur",
        description: t("cms.authentication.detail.status.updateError") || "Erreur lors de la mise à jour du statut",
        variant: "error",
      });
    }
  };

  const tabList = useMemo(
    () =>
      [
        {
          label: t("cms.authentication.details.tabs.sendPhotos"),
          component: (_props: AuthenticationFormProps) => {
            return <DocumentList documents={submission?.images ?? []} instanceId={submission?.instance?.id ?? ''} />;
          },
          value: "form-content",
          publishedSwitch: "published",
        },
        {
          label: t("cms.authentication.details.tabs.sendDocuments"),
          component: (_props: AuthenticationFormProps) => {
            return <DocumentList documents={submission?.documents ?? []} instanceId={submission?.instance?.id ?? ''} />;
          },
          value: "form-list",
          publishedSwitch: "publishedList",
        }
      ] satisfies {
        label: string;
        value: string;
        publishedSwitch: string;
        component: (props: AuthenticationFormProps) => JSX.Element;
      }[],
    [t, submission],
  );
  return (
    <div className="py-11 px-24 gap-8 flex flex-col">
      <div className="flex flex-col justify-between gap-6">
        <div className="flex flex-row items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/cms/artwork-authentication?tab=form-list")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("cms.authentication.detail.back") || "Retour"}
          </Button>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/cms/artwork-authentication?tab=form-list">
                  {t("cmsTabAuthenticationForm") || "Formulaires d'authentification"}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {t("cms.authentication.detail.title") || "Détail de la soumission"}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </div>
        <div className="flex flex-row justify-between">
          <div>
            {/* Titre de la page */}
            <h1 className="text-[#0c111d] text-3xl font-semibold font-['Raleway'] leading-normal mb-6">
              {t("cms.authentication.detail.title") || "Détail de la soumission"}
            </h1>

            <div className="flex flex-row">
              <div className="flex flex-col gap-1 mr-6">
                <p className="text-sm text-muted-foreground">{t("cms.authentication.detail.formNumber")}</p>
                <p className="text-base">{ submission?.id.split('-')[0] }</p>
              </div>
              <Separator orientation="vertical" className="h-auto w-[0.5px] bg-gray-300" />
              <div className="flex flex-col gap-1 mx-6">
                <p className="text-sm text-muted-foreground">{t("cms.authentication.detail.receptionDate")}</p>
                <p className="text-base">{submission?.createdAt instanceof Date
                      ? submission?.createdAt?.toLocaleDateString()
                      : new Date(submission?.createdAt ?? '').toLocaleDateString()}</p>
              </div>
              <Separator orientation="vertical" className="h-auto w-[0.5px] bg-gray-300" />
              <div className="flex flex-col gap-1 ml-6">
                <p className="text-sm text-muted-foreground">{t("cms.authentication.detail.holderInformation")}</p>
                <p className="text-base">{submission?.holderName}</p>
                <p className="text-base">{submission?.holderEmail}</p>
                <p className="text-base">{submission?.phone}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-4 items-end">
          <Select
              value={submission?.isPaid === true ? "paid" : "unpaid"}
              onValueChange={(value) => updateStatus(value === "paid")}
            >
              <SelectTrigger
                className={`
                  h-auto px-3 py-1 rounded-2xl border text-xs font-medium font-['Raleway']
                  ${submission?.isPaid === true
                    ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100" 
                    : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                  }
                  focus:ring-2 focus:ring-offset-2
                  ${submission?.isPaid === true ? "focus:ring-green-500" : "focus:ring-red-500"}
                  w-fit
                `}
              >
                <SelectValue>
                  {submission?.isPaid === true
                    ? (t("cms.authentication.list.table.paid") || "Payé")
                    : (t("cms.authentication.list.table.unpaid") || "Non payé")
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">
                  {t("cms.authentication.list.table.paid") || "Payé"}
                </SelectItem>
                <SelectItem value="unpaid">
                  {t("cms.authentication.list.table.unpaid") || "Non payé"}
                </SelectItem>
              </SelectContent>
            </Select>
            <Button className="flex items-center" onClick={() => downloadPdf(submission?.id)}>
              {t("cms.authentication.detail.button.downloadPdf") || "Télécharger le PDF signé"}
            </Button>
          </div>
        </div>
        <Separator className="my-6 w-full h-[1px] bg-gray-300" orientation="horizontal" />
        <div>
          {/* tabs des documents */}
          <Tabs className="w-full" defaultValue="form-content">
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="inline-flex gap-8 w-full data-[state=inactive]:hidden flex-col"
                >
                  <Comp language={getMainLanguage() as SupportedLocale} instanceId={submission?.instance.id ?? ''} />
                  {/* <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${lang}.${tab.publishedSwitch}`}
                      />
                    )}
                    render={(lang) => <Comp language={lang} />}
                  /> */}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ArtworkAuthenticationDetailCMS;

